import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Process.css';

gsap.registerPlugin(ScrollTrigger);

const steps = [
  {
    num: '01',
    title: 'Rencontre & Écoute',
    desc: 'On se déplace chez vous. On écoute, on observe, on comprend votre espace et vos envies.',
  },
  {
    num: '02',
    title: 'Conception 3D',
    desc: 'Nos designers donnent vie à votre projet en plans et visuels 3D réalistes. Vous voyez avant de valider.',
  },
  {
    num: '03',
    title: 'Fabrication',
    desc: 'Chaque pièce prend forme dans notre atelier. Bois sélectionné, machines de précision, finitions main.',
  },
  {
    num: '04',
    title: 'Livraison & Pose',
    desc: 'Installation soignée, dans les délais. On ne part qu\'une fois que tout est parfait.',
  },
];

export default function Process() {
  const sectionRef = useRef(null);
  const lineRef = useRef(null);
  const stepsRef = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {

      // Animate the golden line drawing down
      gsap.fromTo(lineRef.current,
        { scaleY: 0 },
        {
          scaleY: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 60%',
            end: 'bottom 80%',
            scrub: 1,
          },
        }
      );

      // Staggered step reveals
      stepsRef.current.forEach((step, i) => {
        if (!step) return;

        const num = step.querySelector('.proc-step__num');
        const content = step.querySelector('.proc-step__content');
        const dot = step.querySelector('.proc-step__dot');

        // Dot pulse animation
        gsap.fromTo(dot,
          { scale: 0, opacity: 0 },
          {
            scale: 1,
            opacity: 1,
            duration: 0.6,
            ease: 'back.out(2)',
            scrollTrigger: {
              trigger: step,
              start: 'top 75%',
              toggleActions: 'play none none reverse',
            },
          }
        );

        // Number slide in from left
        gsap.fromTo(num,
          { x: -40, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            duration: 0.8,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: step,
              start: 'top 75%',
              toggleActions: 'play none none reverse',
            },
          }
        );

        // Content slide in from right
        gsap.fromTo(content,
          { x: 40, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            duration: 0.8,
            delay: 0.15,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: step,
              start: 'top 75%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="proc-section" ref={sectionRef}>
      <div className="proc-container">

        {/* Header */}
        <div className="proc-header">
          <span className="proc-label">Comment ça marche</span>
          <h2 className="proc-title">
            NOTRE<br/>
            <em>PROCESSUS.</em>
          </h2>
        </div>

        {/* Timeline */}
        <div className="proc-timeline">
          {/* Golden progress line */}
          <div className="proc-timeline__track">
            <div className="proc-timeline__line" ref={lineRef} />
          </div>

          {/* Steps */}
          {steps.map((step, i) => (
            <div
              key={i}
              className={`proc-step ${i % 2 === 1 ? 'proc-step--right' : ''}`}
              ref={el => (stepsRef.current[i] = el)}
            >
              {/* Dot on the line */}
              <div className="proc-step__dot" />

              {/* Number */}
              <span className="proc-step__num">{step.num}</span>

              {/* Content */}
              <div className="proc-step__content">
                <h3 className="proc-step__title">{step.title}</h3>
                <p className="proc-step__desc">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
